import { useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

interface PageBookingCtaProps {
  headline?: string;
  accentLine?: string;
  description?: string;
}

export default function PageBookingCta({
  headline = 'Book Your Ride',
  accentLine = 'Today',
  description = 'Reliable, professional luxury transportation is just a click away. Fill out the form or call us directly for immediate booking and pricing.',
}: PageBookingCtaProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const ref = useScrollReveal<HTMLDivElement>();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fields = form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea');
    fields.forEach((f) => { if (f.name && f.value) data.append(f.name, f.value); });
    try {
      await fetch('https://readdy.ai/api/form/d6va4judp0k8rarn7tr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-28" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className="section-reveal">
          {/* Section header */}
          <div className="text-center mb-14">
            <span className="text-gold text-xs tracking-widest uppercase font-semibold">Ready to Ride?</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-3">
              {headline}<br />
              <span className="text-gold">{accentLine}</span>
            </h2>
            <p className="text-white/45 text-sm leading-relaxed mt-5 max-w-xl mx-auto">{description}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: contact options */}
            <div>
              <div className="space-y-4 mb-10">
                <a href="tel:+18436422440" className="flex items-center gap-4 p-5 border border-gold/20 hover:border-gold bg-white/2 hover:bg-gold/5 transition-all cursor-pointer group">
                  <div className="w-14 h-14 bg-gold/15 group-hover:bg-gold flex items-center justify-center transition-colors flex-shrink-0">
                    <i className="ri-phone-fill text-gold group-hover:text-black text-2xl transition-colors"></i>
                  </div>
                  <div>
                    <div className="text-white/40 text-xs uppercase tracking-widest mb-0.5">Primary Line — Book Now</div>
                    <div className="text-white font-bold text-2xl tracking-wide group-hover:text-gold transition-colors">(843) 642-2440</div>
                  </div>
                </a>
                <div className="grid grid-cols-2 gap-4">
                  <a href="tel:+18435130018" className="flex items-center gap-3 p-4 border border-white/8 hover:border-gold/30 bg-white/2 hover:bg-gold/5 transition-all cursor-pointer group">
                    <div className="w-10 h-10 bg-white/5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-white/35 group-hover:text-gold text-lg transition-colors"></i>
                    </div>
                    <div>
                      <div className="text-white/30 text-xs">Secondary</div>
                      <div className="text-white/65 group-hover:text-white text-sm font-semibold transition-colors">(843) 513-0018</div>
                    </div>
                  </a>
                  <a href="mailto:tecslimoservice@gmail.com" className="flex items-center gap-3 p-4 border border-white/8 hover:border-gold/30 bg-white/2 hover:bg-gold/5 transition-all cursor-pointer group">
                    <div className="w-10 h-10 bg-white/5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-white/35 group-hover:text-gold text-lg transition-colors"></i>
                    </div>
                    <div>
                      <div className="text-white/30 text-xs">Email Us</div>
                      <div className="text-white/65 group-hover:text-white text-xs font-medium transition-colors break-all">tecslimoservice@gmail.com</div>
                    </div>
                  </a>
                </div>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-shield-check-fill', text: 'Licensed & Insured' },
                  { icon: 'ri-time-fill', text: 'Available 24/7' },
                  { icon: 'ri-user-star-fill', text: 'Background Checked' },
                  { icon: 'ri-star-fill', text: '5-Star Rated' },
                ].map((b) => (
                  <div key={b.text} className="flex items-center gap-2.5 p-3 border border-white/5">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`${b.icon} text-gold text-sm`}></i>
                    </div>
                    <span className="text-white/50 text-xs font-medium">{b.text}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 border-l-2 border-gold pl-4">
                <p className="text-white/35 text-xs leading-relaxed">
                  <strong className="text-white/55">Available 24/7</strong> — Early morning airport pickups, late-night events, weekend bookings. We&apos;re always here when you need us.
                </p>
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-neutral-900 border border-gold/15 p-8">
              <h3 className="font-display text-2xl font-bold text-white mb-1">Request a Free Quote</h3>
              <p className="text-white/35 text-xs mb-8 leading-relaxed">Fill out the form and we&apos;ll respond with pricing and availability.</p>

              {submitted ? (
                <div className="text-center py-14">
                  <div className="w-16 h-16 bg-gold/15 flex items-center justify-center mx-auto mb-5">
                    <i className="ri-check-double-line text-gold text-3xl"></i>
                  </div>
                  <h4 className="text-white font-semibold text-lg mb-2">Request Received!</h4>
                  <p className="text-white/45 text-sm">We&apos;ll be in touch shortly. For immediate service, call <a href="tel:+18436422440" className="text-gold hover:text-gold-light cursor-pointer">(843) 642-2440</a>.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="booking-form" className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/40 text-xs uppercase tracking-widest mb-1.5">Name *</label>
                      <input type="text" name="name" required placeholder="Full name" className="w-full bg-white/5 border border-white/8 focus:border-gold px-4 py-3 text-white text-sm placeholder-white/20 outline-none transition-colors" />
                    </div>
                    <div>
                      <label className="block text-white/40 text-xs uppercase tracking-widest mb-1.5">Phone *</label>
                      <input type="tel" name="phone" required placeholder="(000) 000-0000" className="w-full bg-white/5 border border-white/8 focus:border-gold px-4 py-3 text-white text-sm placeholder-white/20 outline-none transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs uppercase tracking-widest mb-1.5">Email</label>
                    <input type="email" name="email" placeholder="your@email.com" className="w-full bg-white/5 border border-white/8 focus:border-gold px-4 py-3 text-white text-sm placeholder-white/20 outline-none transition-colors" />
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs uppercase tracking-widest mb-1.5">Pickup Location *</label>
                    <input type="text" name="pickup_location" required placeholder="Address or airport terminal" className="w-full bg-white/5 border border-white/8 focus:border-gold px-4 py-3 text-white text-sm placeholder-white/20 outline-none transition-colors" />
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs uppercase tracking-widest mb-1.5">Destination *</label>
                    <input type="text" name="destination" required placeholder="Drop-off address or venue" className="w-full bg-white/5 border border-white/8 focus:border-gold px-4 py-3 text-white text-sm placeholder-white/20 outline-none transition-colors" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/40 text-xs uppercase tracking-widest mb-1.5">Date *</label>
                      <input type="date" name="date" required className="w-full bg-white/5 border border-white/8 focus:border-gold px-4 py-3 text-white text-sm outline-none transition-colors" style={{ colorScheme: 'dark' }} />
                    </div>
                    <div>
                      <label className="block text-white/40 text-xs uppercase tracking-widest mb-1.5">Time *</label>
                      <input type="time" name="time" required className="w-full bg-white/5 border border-white/8 focus:border-gold px-4 py-3 text-white text-sm outline-none transition-colors" style={{ colorScheme: 'dark' }} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs uppercase tracking-widest mb-1.5">Service Type</label>
                    <select name="service_type" className="w-full bg-white/5 border border-white/8 focus:border-gold px-4 py-3 text-white text-sm outline-none transition-colors cursor-pointer" style={{ colorScheme: 'dark' }}>
                      <option value="" className="bg-neutral-900">Select a service</option>
                      <option value="Airport Transportation" className="bg-neutral-900">Airport Transportation</option>
                      <option value="Corporate Travel" className="bg-neutral-900">Corporate Travel</option>
                      <option value="Wedding Transportation" className="bg-neutral-900">Wedding Transportation</option>
                      <option value="Event Transportation" className="bg-neutral-900">Event Transportation</option>
                      <option value="Prom Transportation" className="bg-neutral-900">Prom Transportation</option>
                      <option value="Private Chauffeur" className="bg-neutral-900">Private Chauffeur Service</option>
                      <option value="Charleston Sights Tour" className="bg-neutral-900">Charleston Sights Tour</option>
                      <option value="Cruises Port Transport" className="bg-neutral-900">Cruises &amp; Port Transport</option>
                    </select>
                  </div>
                  <button type="submit" disabled={submitting} className="w-full bg-gold hover:bg-gold-light disabled:opacity-60 transition-colors text-black font-bold py-4 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer">
                    {submitting ? 'Sending...' : 'Request My Free Quote'}
                  </button>
                  <p className="text-white/20 text-xs text-center">
                    Or call directly: <a href="tel:+18436422440" className="text-gold hover:text-gold-light cursor-pointer">(843) 642-2440</a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
